import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useNotifications } from "../../hooks/useNotifications";
import NotificationBellButton from "./NotificationBellButton";
import { formatDateTime } from "../../utils/dateFormat";
import { formatStatus } from "../../utils/statusLabels";
import api from "../../services/api";
import { messengerConversationPath } from "../../utils/messengerPaths";
import { registerPushNotifications, isPushTokenRegisteredLocally, clearMessagingClientState, refreshFcmTokenForTest, showLocalTestNotification, isChromePushBrowser } from "../../services/pushNotifications";
import { showToast } from "../../utils/toast";
import "./NotificationToolbarBell.css";

function notificationsPathForRole(role) {
  if (role === "CAREGIVER") return "/caregiver/notifications";
  if (role === "CARE_MANAGER") return "/care-manager/notifications";
  if (role === "ADMIN") return "/admin/notifications";
  return "/notifications";
}

function targetPathForNotification(role, item) {
  const payload = item?.payload && typeof item.payload === "object" ? item.payload : {};
  if (typeof payload.nextPath === "string" && payload.nextPath.startsWith("/")) {
    return payload.nextPath;
  }
  if (typeof payload.link === "string" && payload.link.startsWith("/")) {
    return payload.link;
  }
  const type = String(item?.type || item?.notificationType || payload.type || "").toUpperCase();
  const senderUserId = payload.senderUserId;
  if (type === "MESSENGER_MESSAGE" && senderUserId) {
    return messengerConversationPath(role, senderUserId);
  }
  const bookingId = item?.bookingId;
  if (bookingId) {
    if (role === "CAREGIVER") {
      return `/caregiver/start-end-service?booking=${encodeURIComponent(bookingId)}`;
    }
    if (role === "CARE_MANAGER") {
      return `/care-manager/exceptions?booking=${encodeURIComponent(bookingId)}`;
    }
    if (role === "ADMIN") {
      return `/admin/exceptions?booking=${encodeURIComponent(bookingId)}`;
    }
    return `/my-bookings`;
  }
  return notificationsPathForRole(role);
}

function withUiTimeout(promise, ms, label) {
  return Promise.race([
    promise,
    new Promise((_, reject) => {
      window.setTimeout(() => {
        reject(new Error(`${label} timed out — check nemacareBE is running and retry.`));
      }, ms);
    }),
  ]);
}

/**
 * Toolbar bell with in-app dropdown — customer, caregiver, care manager, ops.
 */
export default function NotificationToolbarBell() {
  const navigate = useNavigate();
  const panelRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [pushBusy, setPushBusy] = useState("");
  const [pushStatus, setPushStatus] = useState(null);
  const role = api.getAuthRoleFromToken?.() || "";
  const path = notificationsPathForRole(role);
  const pollMs = role === "ADMIN" ? 15000 : 20000;
  const { notifications, unreadCount, loading, load, markRead, markAllRead } = useNotifications({
    limit: 12,
    pollMs,
  });

  const preview = useMemo(() => {
    return [...notifications]
      .sort((a, b) => {
        const ta = new Date(a.sentAt || a.createdAt || 0).getTime();
        const tb = new Date(b.sentAt || b.createdAt || 0).getTime();
        return tb - ta;
      })
      .slice(0, 8);
  }, [notifications]);

  const refreshPushStatus = useCallback(async () => {
    try {
      const res = await api.getPushStatus();
      setPushStatus(res || null);
    } catch {
      setPushStatus(null);
    }
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    refreshPushStatus();
    function onPointerDown(event) {
      if (panelRef.current && !panelRef.current.contains(event.target)) {
        setOpen(false);
      }
    }
    function onKeyDown(event) {
      if (event.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open, refreshPushStatus]);

  async function enableBrowserPush({ hardReset = false } = {}) {
    setPushBusy("enable");
    try {
      if (hardReset) {
        await clearMessagingClientState();
      }
      const result = await withUiTimeout(
        registerPushNotifications({ force: true }),
        35000,
        "Enable push"
      );
      await refreshPushStatus();
      if (result?.ok) {
        showToast.success(
          isChromePushBrowser()
            ? "Chrome push enabled — check Windows notifications under Google Chrome (not Edge)."
            : "Browser notifications enabled for this device."
        );
      } else if (result?.reason === "denied") {
        showToast.error("Notifications blocked — allow them in your browser site settings.");
      } else if (result?.reason === "not_configured") {
        showToast.error("Push is not configured on the server yet.");
      } else {
        showToast.info(`Push registration: ${result?.reason || "could not complete"}`);
      }
    } catch (err) {
      showToast.error(err?.message || "Unable to enable browser push.");
    } finally {
      setPushBusy("");
    }
  }

  async function sendTestPush() {
    setPushBusy("test");
    try {
      console.info("[push] refreshing token before test…");
      const token = await withUiTimeout(refreshFcmTokenForTest(), 35000, "Refresh push token");
      if (!token) {
        showToast.error("Could not refresh push token — click Re-register push, then try again.");
        return;
      }
      console.info("[push] sending test push…");
      const res = await withUiTimeout(api.sendTestPush({ token }), 25000, "Test push");
      await refreshPushStatus();
      if (Number(res?.sent) > 0) {
        const localShown = await showLocalTestNotification(
          "Nema Care test",
          "If you see this in Windows, Chrome notifications work."
        );
        if (localShown) {
          showToast.success(
            isChromePushBrowser()
              ? "Test sent — check Windows tray under Google Chrome (minimize Chrome)."
              : "Test push sent — minimize the browser and check Windows notifications."
          );
        } else {
          showToast.error(
            "FCM delivered but Windows blocked the alert. Enable Google Chrome in Windows Settings → Notifications, then retry Test Windows alert."
          );
        }
      } else {
        const errs = Array.isArray(res?.deliveryErrors) ? res.deliveryErrors.join(", ") : "";
        const localShown = await showLocalTestNotification(
          "Nema Care local test",
          "Chrome can show notifications from this site."
        );
        if (localShown) {
          showToast.info(
            `Server push failed${errs ? ` (${errs})` : ""}. A local test notification was shown — check the Windows tray. Click Reset push, then Re-register push.`
          );
        } else {
          showToast.error(
            res?.message ||
              `Server push failed${errs ? ` (${errs})` : ""}. Click Reset push, then Re-register push.`
          );
        }
      }
    } catch (err) {
      showToast.error(err?.message || "Test push failed.");
    } finally {
      setPushBusy("");
    }
  }

  if (!["CUSTOMER", "CAREGIVER", "CARE_MANAGER", "ADMIN", "OPS"].includes(role)) {
    return null;
  }

  const localPushReady = isPushTokenRegisteredLocally();

  async function openNotification(item) {
    if (!item?.readAt && item?.id) {
      markRead([item.id]);
    }
    setOpen(false);
    navigate(targetPathForNotification(role, item));
  }

  return (
    <div className="notif-toolbar" ref={panelRef}>
      <NotificationBellButton
        unreadCount={unreadCount}
        open={open}
        onClick={() => {
          setOpen((value) => !value);
          if (!open) load();
        }}
        title="Notifications"
      />

      {open ? (
        <div className="notif-toolbar__panel" role="dialog" aria-label="Notifications">
          <header className="notif-toolbar__head">
            <h2>Notifications{unreadCount > 0 ? ` (${unreadCount})` : ""}</h2>
            <div className="notif-toolbar__head-actions">
              <button type="button" className="notif-toolbar__text-btn" onClick={() => load()}>
                Refresh
              </button>
              {unreadCount > 0 ? (
                <button type="button" className="notif-toolbar__text-btn" onClick={() => markAllRead()}>
                  Mark all read
                </button>
              ) : null}
            </div>
          </header>

          <div className="notif-toolbar__push">
            <p className="notif-toolbar__push-label">
              {localPushReady
                ? "Browser push enabled in this browser"
                : pushStatus?.hasToken
                  ? "Push is enabled in another browser — click Enable push here for Chrome/Edge on this device"
                  : pushStatus?.webEnabled === false
                    ? "Server push not configured"
                    : "Enable browser push for booking and message alerts"}
            </p>
            <div className="notif-toolbar__push-actions">
              <button
                type="button"
                className="notif-toolbar__text-btn"
                onClick={() => enableBrowserPush()}
                disabled={pushBusy !== ""}
              >
                {pushBusy === "enable"
                  ? "Enabling…"
                  : localPushReady
                    ? "Re-register push"
                    : "Enable push"}
              </button>
              {localPushReady ? (
                <button
                  type="button"
                  className="notif-toolbar__text-btn"
                  onClick={sendTestPush}
                  disabled={pushBusy !== ""}
                >
                  {pushBusy === "test" ? "Sending…" : "Test push"}
                </button>
              ) : null}
              <button
                type="button"
                className="notif-toolbar__text-btn"
                onClick={async () => {
                  setPushBusy("local");
                  const ok = await showLocalTestNotification(
                    "Nema Care",
                    "Direct Windows test — via Google Chrome"
                  );
                  setPushBusy("");
                  showToast[ok ? "success" : "error"](
                    ok
                      ? "Check bottom-right Windows tray — it should say via Google Chrome."
                      : "Chrome blocked the alert. Open chrome://settings/content/notifications, allow localhost, then reload."
                  );
                }}
                disabled={pushBusy !== ""}
              >
                {pushBusy === "local" ? "Testing…" : "Test Windows alert"}
              </button>
              <button
                type="button"
                className="notif-toolbar__text-btn"
                onClick={() => enableBrowserPush({ hardReset: true })}
                disabled={pushBusy !== ""}
                title="Clear push cache and register again"
              >
                {pushBusy === "enable" ? "Working…" : "Reset push"}
              </button>
            </div>
          </div>

          <div className="notif-toolbar__body">
            {loading && preview.length === 0 ? (
              <p className="notif-toolbar__empty">Loading notifications…</p>
            ) : null}
            {!loading && preview.length === 0 ? (
              <p className="notif-toolbar__empty">
                No notifications yet. Booking accepts, OTP updates, and cancel requests appear here.
              </p>
            ) : null}
            {preview.map((item) => (
              <button
                key={item.id}
                type="button"
                className={`notif-toolbar__item${!item.readAt ? " notif-toolbar__item--unread" : ""}`}
                onClick={() => openNotification(item)}
              >
                <p className="notif-toolbar__item-title">{item.title}</p>
                {item.body ? <p className="notif-toolbar__item-body">{item.body}</p> : null}
                <p className="notif-toolbar__item-meta">
                  {formatStatus(item.type)}
                  {item.bookingId ? ` · Booking #${item.bookingId}` : ""}
                  {" · "}
                  {formatDateTime(item.sentAt || item.createdAt)}
                </p>
              </button>
            ))}
          </div>

          <footer className="notif-toolbar__foot">
            <button
              type="button"
              className="care-btn care-btn--primary"
              onClick={() => {
                setOpen(false);
                navigate(path);
              }}
            >
              View all notifications
            </button>
          </footer>
        </div>
      ) : null}
    </div>
  );
}
